import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CaretDown, CaretRight } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';

export default function CollapsibleItinerarySelector({
  items = [],
  selectedIds = [],
  onChange,
  title = 'Itinerary Events',
  description = '',
  emptyMessage = 'Add itinerary events first to choose event days.',
  idPrefix = 'itinerary',
  required = false,
  defaultSelectAll = true,
  renderPrimaryLabel,
  renderSecondaryLabel,
  className = '',
  listClassName = '',
  optionClassName = '',
  summary,
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const initializedOptionsRef = useRef('');
  const optionIds = useMemo(() => items.map((item) => item?.id).filter(Boolean), [items]);
  const optionKey = optionIds.join('|');
  const selectedSet = useMemo(() => new Set((selectedIds || []).filter(Boolean)), [selectedIds]);
  const allSelected = optionIds.length > 0 && optionIds.every((itemId) => selectedSet.has(itemId));
  const partiallySelected = !allSelected && selectedSet.size > 0;

  useEffect(() => {
    if (!defaultSelectAll || optionIds.length === 0 || !onChange) return;
    if (initializedOptionsRef.current === optionKey) return;
    initializedOptionsRef.current = optionKey;
    if ((selectedIds || []).filter(Boolean).length > 0) return;
    onChange(optionIds);
  }, [defaultSelectAll, onChange, optionIds, optionKey, selectedIds]);

  const updateSelection = (nextIds) => {
    onChange?.(items.map((item) => item.id).filter((itemId) => nextIds.includes(itemId)));
  };

  const toggleItem = (itemId, checked) => {
    const nextIds = new Set(selectedSet);
    if (checked) nextIds.add(itemId);
    else nextIds.delete(itemId);
    updateSelection(Array.from(nextIds));
  };

  if (items.length === 0) {
    return (
      <div className={`rounded-md border border-dashed p-3 text-sm text-muted-foreground ${className}`}>
        {emptyMessage}
      </div>
    );
  }

  return (
    <fieldset className={`rounded-md border p-2.5 ${className}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <legend className="text-sm font-semibold text-wedding-charcoal">
            {title}{required ? ' *' : ''}
          </legend>
          {description && (
            <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
          )}
          <p className="mt-1 truncate text-xs text-muted-foreground">
            {summary || `${selectedSet.size} of ${items.length} selected`}
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 shrink-0 px-2 text-xs"
          onClick={() => setIsExpanded((current) => !current)}
        >
          {isExpanded ? <CaretDown className="mr-1 h-3.5 w-3.5" /> : <CaretRight className="mr-1 h-3.5 w-3.5" />}
          {isExpanded ? 'Hide events' : 'Show / Edit events'}
        </Button>
      </div>

      {isExpanded && (
        <div className={`mt-2 max-h-32 overflow-y-auto rounded-md border bg-background ${listClassName}`} data-testid={`${idPrefix}-list`}>
          <label
            htmlFor={`${idPrefix}-all`}
            className="flex min-h-8 cursor-pointer items-center gap-2 border-b bg-muted/30 px-2.5 py-1.5 text-sm font-medium"
          >
            <Checkbox
              id={`${idPrefix}-all`}
              checked={allSelected ? true : partiallySelected ? 'indeterminate' : false}
              onCheckedChange={(checked) => updateSelection(checked === true ? optionIds : [])}
              aria-label="Select all itinerary events"
              data-testid={`${idPrefix}-select-all`}
            />
            <span>All itinerary events</span>
          </label>
          {items.map((item) => {
            const checkboxId = `${idPrefix}-${item.id}`;
            const isSelected = selectedSet.has(item.id);
            return (
              <label
                key={item.id}
                htmlFor={checkboxId}
                className={`flex min-h-8 cursor-pointer items-start gap-2 border-b px-2.5 py-1.5 text-sm last:border-b-0 hover:bg-muted/20 ${optionClassName}`}
              >
                <Checkbox
                  id={checkboxId}
                  checked={isSelected}
                  onCheckedChange={(checked) => toggleItem(item.id, checked === true)}
                  className="mt-0.5"
                  aria-label={`Select ${renderPrimaryLabel ? renderPrimaryLabel(item) : item.name || 'event day'}`}
                  data-testid={`${idPrefix}-${item.id}`}
                />
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-wedding-charcoal">
                    {renderPrimaryLabel ? renderPrimaryLabel(item) : item.name || 'Event day'}
                  </span>
                  {renderSecondaryLabel && (
                    <span className="block text-xs text-muted-foreground">
                      {renderSecondaryLabel(item)}
                    </span>
                  )}
                </span>
              </label>
            );
          })}
        </div>
      )}
    </fieldset>
  );
}
